import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Book } from '@/types'

interface RouletteWheelProps {
  books: Book[]
  isSpinning: boolean
  selectedBookId?: string
  onSpinComplete?: (bookId: string) => void
  duration?: number
}

export function RouletteWheel({
  books,
  isSpinning,
  selectedBookId,
  onSpinComplete,
  duration = 4000,
}: RouletteWheelProps) {
  const { t } = useTranslation()
  const wheelRef = useRef<SVGSVGElement>(null)
  const [rotation, setRotation] = useState(0)
  const [animationClass, setAnimationClass] = useState('')

  // Calculate the angle for each book segment
  const segmentAngle = 360 / books.length
  const centerX = 200
  const centerY = 200
  const radius = 160

  useEffect(() => {
    if (isSpinning && books.length > 0) {
      // Calculate target rotation
      const selectedIndex = selectedBookId
        ? books.findIndex((book) => book.id === selectedBookId)
        : Math.floor(Math.random() * books.length)

      // Add multiple full rotations for effect + position for selected book
      const baseRotations = 360 * (3 + Math.random() * 2) // 3-5 full rotations
      const targetAngle = selectedIndex * segmentAngle
      const finalRotation = baseRotations - targetAngle // Negative because wheel spins counter-clockwise

      setAnimationClass('animate-spin-roulette')
      setRotation(finalRotation)

      // Complete animation after duration
      const timeout = setTimeout(() => {
        setAnimationClass('')
        if (onSpinComplete && selectedBookId) {
          onSpinComplete(selectedBookId)
        } else if (onSpinComplete) {
          const finalSelectedIndex =
            selectedIndex >= 0 ? selectedIndex : Math.floor(Math.random() * books.length)
          onSpinComplete(books[finalSelectedIndex].id)
        }
      }, duration)

      return () => clearTimeout(timeout)
    }
  }, [isSpinning, books, selectedBookId, duration, onSpinComplete, segmentAngle])

  const getBookPosition = (index: number) => {
    // Calculate the center angle of the segment
    const centerAngle = (index * segmentAngle + segmentAngle / 2 - 90) * (Math.PI / 180)
    // Position text at 60% of radius for better centering
    const textRadius = radius * 0.6
    const x = centerX + textRadius * Math.cos(centerAngle)
    const y = centerY + textRadius * Math.sin(centerAngle)
    // Rotate text to match segment angle
    const textAngle = index * segmentAngle + segmentAngle / 2

    return { x, y, textAngle }
  }

  const getSegmentPath = (index: number) => {
    const startAngle = (index * segmentAngle - 90) * (Math.PI / 180)
    const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180)

    const x1 = centerX + radius * Math.cos(startAngle)
    const y1 = centerY + radius * Math.sin(startAngle)
    const x2 = centerX + radius * Math.cos(endAngle)
    const y2 = centerY + radius * Math.sin(endAngle)

    const largeArcFlag = segmentAngle > 180 ? 1 : 0

    return `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`
  }

  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FFEAA7',
    '#DDA0DD',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E9',
    '#F8C471',
    '#82E0AA',
    '#F1948A',
    '#FD79A8',
    '#A29BFE',
    '#FDCB6E',
    '#6C5CE7',
    '#FAB1A0',
    '#74B9FF',
    '#A8E6CF',
  ]

  return (
    <div className="flex flex-col items-center space-y-8">
      {/* Wheel Container */}
      <div className="relative">
        {/* Pointer */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2 z-20">
          <div className="w-0 h-0 border-l-[15px] border-r-[15px] border-b-[30px] border-l-transparent border-r-transparent border-b-amber drop-shadow-lg"></div>
        </div>

        {/* Wheel */}
        <div className="relative">
          <svg
            ref={wheelRef}
            width="400"
            height="400"
            className={`transition-transform ${animationClass} drop-shadow-2xl`}
            style={{
              transform: `rotate(${rotation}deg)`,
              transitionDuration: isSpinning ? `${duration}ms` : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
            }}
          >
            {/* Wheel segments */}
            {books.map((book, index) => (
              <g key={book.id}>
                {/* Segment background */}
                <path
                  d={getSegmentPath(index)}
                  fill={colors[index % colors.length]}
                  stroke="#1f2937"
                  strokeWidth="3"
                  className="drop-shadow-sm"
                />

                {/* Book title text */}
                <text
                  x={getBookPosition(index).x}
                  y={getBookPosition(index).y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="white"
                  fontSize="14"
                  fontWeight="bold"
                  className="pointer-events-none select-none"
                  style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                  transform={`rotate(${getBookPosition(index).textAngle}, ${getBookPosition(index).x}, ${getBookPosition(index).y})`}
                >
                  {book.title.length > 18 ? `${book.title.substring(0, 18)}...` : book.title}
                </text>
              </g>
            ))}

            {/* Center circle */}
            <circle
              cx={centerX}
              cy={centerY}
              r="30"
              fill="url(#centerGradient)"
              stroke="#333"
              strokeWidth="3"
              className="drop-shadow-lg"
            />

            {/* Gradient definitions */}
            <defs>
              <radialGradient id="centerGradient" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="100%" stopColor="#FFA500" />
              </radialGradient>
            </defs>

            {/* Center icon */}
            <text
              x={centerX}
              y={centerY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="20"
              className="pointer-events-none select-none"
            >
              🎲
            </text>
          </svg>
        </div>

        {/* Outer decorative ring */}
        <div className="absolute inset-0 rounded-full border-8 border-primary/20 opacity-30"></div>
      </div>

      {/* Status and Controls */}
      <div className="text-center space-y-4">
        {isSpinning ? (
          <div className="space-y-2">
            <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary-dark">
              🎰 {t('roulette.spinning')}...
            </div>
            <div className="flex justify-center">
              <div className="animate-pulse flex space-x-1">
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-secondary rounded-full animate-bounce"
                  style={{ animationDelay: '0.1s' }}
                ></div>
                <div
                  className="w-2 h-2 bg-primary rounded-full animate-bounce"
                  style={{ animationDelay: '0.2s' }}
                ></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-xl font-semibold text-primary-foreground">
              {t('roulette.readyToSpin')}
            </div>
            <div className="text-sm text-primary-foreground/70">
              {t('roulette.booksInRoulette', { count: books.length })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Add custom CSS for the spin animation
const style = document.createElement('style')
style.textContent = `
  @keyframes spin-roulette {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(var(--rotation, 1080deg)); }
  }
  
  .animate-spin-roulette {
    animation: spin-roulette var(--duration, 4000ms) cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
  }
`
document.head.appendChild(style)
